import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert, ShieldOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { POLLING_INTERVALS } from '@/constants/polling';
import { usePmProperty } from '@/hooks/usePmProperty';
import { useVisibilityPolling } from '@/hooks/useVisibilityPolling';
import {
  getLockdownStatus,
  triggerLockdown,
  liftLockdown,
  listLockdowns,
} from '@/services/api.client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { EmergencyType } from '@/types';

const EMERGENCY_LABELS: Record<EmergencyType, string> = {
  fire: 'Fire',
  security_threat: 'Security Threat',
  medical: 'Medical Emergency',
  evacuation: 'Evacuation',
  other: 'Other',
};

const triggerSchema = z.object({
  lockdownType: z.enum(['fire', 'security_threat', 'medical', 'evacuation', 'other']),
  reason: z.string().optional(),
});
type TriggerValues = z.infer<typeof triggerSchema>;

const liftSchema = z.object({
  reason: z.string().optional(),
});
type LiftValues = z.infer<typeof liftSchema>;

export function EmergencyPage() {
  const pm = usePmProperty();
  const propertyId = pm?.propertyId ?? '';
  const qc = useQueryClient();

  const [triggerOpen, setTriggerOpen] = useState(false);
  const [liftOpen, setLiftOpen] = useState(false);
  const pollInterval = useVisibilityPolling(POLLING_INTERVALS.PM.FAST);

  const statusQuery = useQuery({
    queryKey: ['pm', 'emergency', 'status', propertyId],
    queryFn: () => getLockdownStatus(propertyId),
    enabled: !!propertyId,
    refetchInterval: pollInterval,
  });

  const historyQuery = useQuery({
    queryKey: ['pm', 'emergency', 'history', propertyId],
    queryFn: () => listLockdowns(propertyId),
    enabled: !!propertyId,
  });

  const triggerMutation = useMutation({
    mutationFn: (values: TriggerValues) =>
      triggerLockdown(propertyId, {
        lockdownType: values.lockdownType,
        confirm: true,
        ...(values.reason !== undefined && { reason: values.reason }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'emergency'] });
      setTriggerOpen(false);
      triggerReset();
    },
  });

  const liftMutation = useMutation({
    mutationFn: (values: LiftValues) =>
      liftLockdown(propertyId, {
        confirm: true,
        ...(values.reason !== undefined && { reason: values.reason }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'emergency'] });
      setLiftOpen(false);
      liftReset();
    },
  });

  const {
    handleSubmit: handleTrigger,
    setValue: setTriggerValue,
    register: registerTrigger,
    reset: triggerReset,
  } = useForm<TriggerValues>({
    resolver: zodResolver(triggerSchema),
    defaultValues: { lockdownType: 'security_threat' },
  });

  const {
    register: registerLift,
    handleSubmit: handleLift,
    reset: liftReset,
  } = useForm<LiftValues>({ resolver: zodResolver(liftSchema) });

  const status = statusQuery.data;
  const history = historyQuery.data ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl">Emergency Lockdown</h1>

      {/* Current lockdown status */}
      {statusQuery.isLoading ? (
        <Skeleton className="h-32 rounded-lg" />
      ) : status?.active ? (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-8 w-8 text-destructive" />
            <div>
              <p className="text-lg font-semibold text-destructive">Active Lockdown</p>
              {status.type && (
                <p className="text-sm text-[var(--text-tertiary)]">
                  {EMERGENCY_LABELS[status.type as EmergencyType] ?? status.type}
                  {status.triggeredBy && ` · Triggered by ${status.triggeredBy}`}
                  {status.triggeredAt && ` · ${new Date(status.triggeredAt).toLocaleString()}`}
                </p>
              )}
            </div>
          </div>
          <Button variant="destructive" onClick={() => setLiftOpen(true)}>
            <ShieldOff className="mr-2 h-4 w-4" />
            Lift Lockdown
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-raised)] p-6 space-y-4">
          <div className="flex items-center gap-3">
            <ShieldOff className="h-8 w-8 text-[var(--text-tertiary)]" />
            <div>
              <p className="font-semibold">No Active Lockdown</p>
              <p className="text-sm text-[var(--text-tertiary)]">
                The property is operating normally.
              </p>
            </div>
          </div>
          <Button variant="destructive" onClick={() => setTriggerOpen(true)}>
            <ShieldAlert className="mr-2 h-4 w-4" />
            Trigger Lockdown
          </Button>
        </div>
      )}

      {/* Lockdown history */}
      <div className="space-y-3">
        <h2 className="text-lg">Lockdown History</h2>
        {historyQuery.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-20 rounded-lg" />
            ))}
          </div>
        ) : history.length === 0 ? (
          <p className="text-sm text-[var(--text-tertiary)]">No lockdown history.</p>
        ) : (
          <div className="space-y-3">
            {history.map((record) => (
              <div
                key={record.id}
                className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-raised)] p-4 space-y-1"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-xs">
                    {EMERGENCY_LABELS[record.lockdownType as EmergencyType] ?? record.lockdownType}
                  </Badge>
                  {record.liftedAt ? (
                    <Badge variant="secondary" className="text-xs">
                      Resolved
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="text-xs">
                      Active
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-[var(--text-tertiary)]">
                  Triggered: {new Date(record.triggeredAt).toLocaleString()}
                  {record.liftedAt && ` · Lifted: ${new Date(record.liftedAt).toLocaleString()}`}
                </p>
                {record.reason && (
                  <p className="text-sm text-[var(--text-tertiary)]">{record.reason}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Trigger dialog */}
      <Dialog
        open={triggerOpen}
        onOpenChange={(open) => {
          if (!open) {
            setTriggerOpen(false);
            triggerReset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Trigger Emergency Lockdown</DialogTitle>
            <DialogDescription>
              You are about to lock down the entire property. All gate access will be suspended
              immediately. This action will be logged.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTrigger((v) => triggerMutation.mutate(v))} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="em-type">Emergency Type</Label>
              <Select
                defaultValue="security_threat"
                onValueChange={(v) => setTriggerValue('lockdownType', v as EmergencyType)}
              >
                <SelectTrigger id="em-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fire">Fire</SelectItem>
                  <SelectItem value="security_threat">Security Threat</SelectItem>
                  <SelectItem value="medical">Medical Emergency</SelectItem>
                  <SelectItem value="evacuation">Evacuation</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="trigger-reason">Reason / Notes (optional)</Label>
              <Textarea
                id="trigger-reason"
                rows={3}
                {...registerTrigger('reason')}
                placeholder="Describe the situation…"
              />
            </div>
            {triggerMutation.isError && (
              <p className="text-sm text-destructive">Failed to trigger lockdown. Try again.</p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setTriggerOpen(false);
                  triggerReset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={triggerMutation.isPending}>
                {triggerMutation.isPending ? 'Locking down…' : 'Confirm Lockdown'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lift dialog */}
      <Dialog
        open={liftOpen}
        onOpenChange={(open) => {
          if (!open) {
            setLiftOpen(false);
            liftReset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lift Emergency Lockdown</DialogTitle>
            <DialogDescription>
              Lifting the lockdown will restore normal gate access. Optionally provide a resolution
              note for the audit log.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLift((v) => liftMutation.mutate(v))} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="lift-note">Resolution Note (optional)</Label>
              <Textarea
                id="lift-note"
                rows={3}
                {...registerLift('reason')}
                placeholder="Describe how the situation was resolved…"
              />
            </div>
            {liftMutation.isError && (
              <p className="text-sm text-destructive">Failed to lift lockdown. Try again.</p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setLiftOpen(false);
                  liftReset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={liftMutation.isPending}>
                {liftMutation.isPending ? 'Lifting…' : 'Lift Lockdown'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
